import type { ReactNode } from "react";
import { Avatar, Typography } from "antd";
import {
  BankOutlined,
  BellOutlined,
  BranchesOutlined,
  CarryOutOutlined,
  PictureOutlined,
  ShopOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import Colors from "infrastructure/utils/colors";
import { EventName } from "../types";

const { Text, Title } = Typography;

const Root = styled.div<{ $unread: boolean; $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid
    ${(p) => (p.$unread ? "rgba(68, 200, 116, 0.35)" : Colors.border)};
  background: ${(p) => (p.$unread ? Colors.green_12 : Colors.white)};
  box-shadow: ${(p) =>
    p.$unread ? "0 0 0 1px rgba(68, 200, 116, 0.08) inset" : `0 1px 3px ${Colors.shadow}`};
  margin-bottom: 10px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.18s ease;

  &:hover {
    border-color: ${(p) => (p.$unread ? Colors.cool_green : Colors.light_gray)};
    box-shadow: 0 6px 20px ${Colors.shadow};
    ${(p) => (p.$clickable ? "transform: translateY(-1px);" : "")}
  }

  &:focus-within {
    outline: none;
    box-shadow: 0 0 0 2px ${Colors.dark_mint_2};
  }
`;

const BodyColumn = styled.div<{ $rtl: boolean }>`
  flex: 1;
  min-width: 0;
  text-align: ${(p) => (p.$rtl ? "right" : "left")};
`;

const UnreadDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${Colors.cool_green};
  margin-top: 6px;
  flex-shrink: 0;
`;

const accentForEvent = (eventName: EventName): string => {
  if (eventName.startsWith("job.")) {
    return Colors.blue;
  }
  if (eventName.startsWith("branch.")) {
    return Colors.orange;
  }
  if (eventName.startsWith("wallet.")) {
    return Colors.dark_green;
  }
  if (eventName.startsWith("shopper.")) {
    return Colors.sunflower;
  }
  if (eventName.startsWith("media.")) {
    return Colors.lipstick;
  }
  if (eventName.startsWith("company.") || eventName.startsWith("manager.")) {
    return Colors.charcoal_grey;
  }
  return Colors.mint_gray;
};

const iconForEvent = (eventName: EventName) => {
  if (eventName.startsWith("job.")) {
    return <CarryOutOutlined />;
  }
  if (eventName.startsWith("branch.")) {
    return <BranchesOutlined />;
  }
  if (eventName.startsWith("wallet.")) {
    return <WalletOutlined />;
  }
  if (eventName.startsWith("shopper.")) {
    return <UserOutlined />;
  }
  if (eventName.startsWith("media.")) {
    return <PictureOutlined />;
  }
  if (eventName.startsWith("company.")) {
    return <BankOutlined />;
  }
  if (eventName.startsWith("manager.")) {
    return <ShopOutlined />;
  }
  return <BellOutlined />;
};

const readHttpsUrl = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  if (value.startsWith("https://") || value.startsWith("http://")) {
    return value;
  }
  return undefined;
};

const resolveAvatarSrc = (payload: Record<string, unknown>): string | undefined => {
  const preview = payload.preview;
  if (typeof preview === "object" && preview !== null) {
    const img = readHttpsUrl((preview as Record<string, unknown>).image);
    if (img) {
      return img;
    }
  }
  const actor = payload.actor;
  if (typeof actor === "object" && actor !== null) {
    const img = readHttpsUrl((actor as Record<string, unknown>).image);
    if (img) {
      return img;
    }
  }
  return undefined;
};

export type NotificationEntryCardProps = {
  eventName: EventName;
  payload: Record<string, unknown>;
  title: string;
  body: string;
  relativeTime: string;
  isRead: boolean;
  isRtl: boolean;
  onActivate?: () => void;
  footer?: ReactNode;
};

const NotificationEntryCard = ({
  eventName,
  payload,
  title,
  body,
  relativeTime,
  isRead,
  isRtl,
  onActivate,
  footer,
}: NotificationEntryCardProps) => {
  const clickable = Boolean(onActivate);
  const accent = accentForEvent(eventName);
  const avatarSrc = resolveAvatarSrc(payload);

  const summary = (
    <>
      <Avatar
        size={44}
        src={avatarSrc}
        style={{
          background: avatarSrc ? undefined : `${accent}18`,
          color: avatarSrc ? undefined : accent,
          flexShrink: 0,
        }}
        icon={avatarSrc ? undefined : iconForEvent(eventName)}
      />
      <BodyColumn $rtl={isRtl}>
        <div
          dir={isRtl ? "rtl" : "ltr"}
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            marginBottom: 4,
            gap: 8,
          }}
        >
          {!isRead && <UnreadDot aria-hidden />}
          <Title
            level={5}
            style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 600,
              lineHeight: 1.35,
              color: Colors.charcoal_grey,
            }}
          >
            {title}
          </Title>
        </div>
        <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5, display: "block" }}>
          {body}
        </Text>
        <Text
          type="secondary"
          style={{
            fontSize: 12,
            marginTop: 8,
            display: "block",
            opacity: 0.85,
          }}
        >
          {relativeTime}
        </Text>
      </BodyColumn>
    </>
  );

  if (clickable && onActivate) {
    return (
      <Root $unread={!isRead} $clickable>
        <div
          role="button"
          tabIndex={0}
          onClick={() => onActivate()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onActivate();
            }
          }}
          style={{
            display: "flex",
            flexDirection: isRtl ? "row-reverse" : "row",
            gap: 14,
            width: "100%",
            alignItems: "flex-start",
            outline: "none",
            cursor: "pointer",
          }}
        >
          {summary}
        </div>
        {footer ? <div style={{ width: "100%", marginTop: 4 }}>{footer}</div> : null}
      </Root>
    );
  }

  return (
    <Root $unread={!isRead} $clickable={false}>
      <div
        style={{
          display: "flex",
          flexDirection: isRtl ? "row-reverse" : "row",
          gap: 14,
          width: "100%",
          alignItems: "flex-start",
        }}
      >
        {summary}
      </div>
      {footer ? <div style={{ width: "100%", marginTop: 4 }}>{footer}</div> : null}
    </Root>
  );
};

export default NotificationEntryCard;
